import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    Input,
    NgZone,
    OnDestroy,
    forwardRef,
    inject,
} from '@angular/core'
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms'
import { FaIconComponent } from '@fortawesome/angular-fontawesome'
import {
    faCircle,
    faLocationCrosshairs,
    faMicrophone,
    faSpinner,
    faStop,
    faXmark,
} from '@fortawesome/free-solid-svg-icons'

import {
    SpeechRecognitionEngineState,
    SpeechRecognitionProvider,
    SpeechRecognitionService,
} from '../../services/speech-recognition'
import { getCurrentPosition } from '../../utility/geolocation'

@Component({
    selector: 'app-voice-text-field',
    standalone: true,
    imports: [FormsModule, FaIconComponent],
    templateUrl: './voice-text-field.component.html',
    styleUrls: ['./voice-text-field.component.css'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => VoiceTextFieldComponent),
            multi: true,
        },
    ],
})
export class VoiceTextFieldComponent implements ControlValueAccessor, AfterViewInit, OnDestroy {
    private speechService = inject(SpeechRecognitionService)
    private zone = inject(NgZone)
    private cdr = inject(ChangeDetectorRef)

    @Input() inputMode: 'text' | 'textarea' = 'textarea'
    /** For <label for="..."> association with the inner input/textarea. */
    @Input() inputId = ''
    @Input() rows = 3
    @Input() placeholder = ''
    /** When true, a short `ngAfterViewInit` tick starts dictation if the browser supports it. */
    @Input() autoStart = false
    /** Merge dictated text after existing field content (with a space). */
    @Input() append = true
    @Input() dense = false
    @Input() compact = false
    @Input() showClearButton = true
    @Input() lang = 'en-US'
    /** When true, shows a button that inserts the device's current GPS location as a Google Maps link. */
    @Input() showLocationButton = false

    micIcon = faMicrophone
    stopIcon = faStop
    circleIcon = faCircle
    clearIcon = faXmark
    locationIcon = faLocationCrosshairs
    spinnerIcon = faSpinner

    value = ''
    disabled = false
    engineState: SpeechRecognitionEngineState = 'idle'
    dictationError = ''
    isLocating = false
    locationError = ''

    private readonly provider: SpeechRecognitionProvider = this.speechService.createProvider()
    private prefixAtSessionStart = ''
    private activeSessionId = 0
    private onChange: (v: string) => void = () => undefined
    private onTouchedCb: () => void = () => undefined

    private readonly visibilityHandler = () => {
        if (document.hidden && this.engineState !== 'idle') {
            this.stopRecordingInternal()
        }
    }

    private readonly pageHideHandler = () => {
        if (this.engineState !== 'idle') {
            this.stopRecordingInternal()
        }
    }

    get isDictationSupported(): boolean {
        return this.provider.isSupported
    }

    get showTrailingControls(): boolean {
        return this.provider.isSupported === true
    }

    get showActionButtons(): boolean {
        return this.showTrailingControls || this.showLocationButton
    }

    get isDictationActive(): boolean {
        return this.engineState !== 'idle'
    }

    get isStopping(): boolean {
        return this.engineState === 'stopping'
    }

    ngAfterViewInit() {
        if (typeof document !== 'undefined') {
            document.addEventListener('visibilitychange', this.visibilityHandler)
        }
        if (typeof window !== 'undefined') {
            window.addEventListener('pagehide', this.pageHideHandler)
        }

        if (!this.autoStart || !this.provider.isSupported || this.disabled) return
        queueMicrotask(() => {
            if (this.engineState === 'idle') this.startRecording()
        })
    }

    ngOnDestroy() {
        if (typeof document !== 'undefined') {
            document.removeEventListener('visibilitychange', this.visibilityHandler)
        }
        if (typeof window !== 'undefined') {
            window.removeEventListener('pagehide', this.pageHideHandler)
        }

        this.speechService.unregisterActive(this.provider)
        this.stopRecordingInternal()
    }

    writeValue(v: string | null): void {
        this.value = v ?? ''
    }

    registerOnChange(fn: (v: string) => void): void {
        this.onChange = fn
    }

    registerOnTouched(fn: () => void): void {
        this.onTouchedCb = fn
    }

    notifyTouched(): void {
        this.onTouchedCb()
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled
    }

    onInput(event: Event): void {
        const t = (event.target as HTMLInputElement | HTMLTextAreaElement).value
        this.value = t
        this.onChange(t)
    }

    startRecording(): void {
        if (!this.provider.isSupported || this.disabled) return

        this.stopRecordingInternal()
        this.dictationError = ''
        this.engineState = 'starting'

        if (this.append) {
            this.prefixAtSessionStart = this.value.trimEnd()
        } else {
            this.prefixAtSessionStart = ''
            this.value = ''
            this.onChange('')
        }

        const sessionId = this.activeSessionId

        this.speechService.registerActive(this.provider, () => this.onForceStoppedByAnotherField(sessionId))

        this.provider.start(
            this.lang,
            (final, interim) => {
                if (sessionId !== this.activeSessionId) {
                    return
                }
                this.zone.run(() => {
                    const merged = mergeDictationSegments(this.prefixAtSessionStart, final, interim)
                    this.value = merged
                    this.onChange(merged)
                    this.cdr.markForCheck()
                })
            },
            error => {
                if (sessionId !== this.activeSessionId) {
                    return
                }
                this.zone.run(() => {
                    this.dictationError = error
                    this.engineState = 'idle'
                    this.speechService.unregisterActive(this.provider)
                    this.cdr.markForCheck()
                })
            },
            {
                onStateChange: state => {
                    if (sessionId !== this.activeSessionId) {
                        return
                    }
                    this.zone.run(() => {
                        this.engineState = state
                        if (state === 'idle') {
                            this.speechService.unregisterActive(this.provider)
                        }
                        this.cdr.markForCheck()
                    })
                },
            },
        )
    }

    stopRecording(): void {
        this.stopRecordingInternal()
        this.notifyTouched()
    }

    clearField(): void {
        this.stopRecordingInternal()
        this.value = ''
        this.onChange('')
        this.notifyTouched()
    }

    insertLocation(): void {
        if (this.disabled || this.isLocating) return

        this.isLocating = true
        this.locationError = ''

        getCurrentPosition()
            .then(position => {
                this.zone.run(() => {
                    const merged = this.value.trim() ? `${this.value.trimEnd()}\n${position.mapUrl}` : position.mapUrl
                    this.value = merged
                    this.onChange(merged)
                    this.notifyTouched()
                    this.isLocating = false
                    this.cdr.markForCheck()
                })
            })
            .catch((error: Error) => {
                this.zone.run(() => {
                    this.locationError = error.message || 'Unable to get your current location.'
                    this.isLocating = false
                    this.cdr.markForCheck()
                })
            })
    }

    private onForceStoppedByAnotherField(sessionId: number): void {
        if (sessionId !== this.activeSessionId) {
            return
        }
        this.zone.run(() => {
            this.engineState = 'idle'
            this.cdr.markForCheck()
        })
    }

    private stopRecordingInternal(): void {
        this.activeSessionId++
        this.provider.stop()
    }
}

function mergeDictationSegments(prefix: string, sessionFinal: string, interim: string): string {
    const parts = [prefix.trimEnd(), sessionFinal.trim(), interim.trim()].filter(Boolean)
    return parts.join(' ')
}
